import { getHanziScriptConvertersQueryOptions } from "@-/dev.improve.haniverse.lib.hanzi-script/query-options";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";
import { useCallback } from "react";
import { useTheme, type ResolvedColorScheme } from "@-/dev.improve.haniverse.lib.theme";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useGetGraphemePrevalence } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.prevalence";
import {
  useActorToneColors,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { convertCharacter } from "@-/dev.improve.haniverse.lib.hanzi-script";
import { getGraphemeGlyph, pinyinForGrapheme, toneForPinyin } from "#get-grapheme-glyph";

export type UseGetGraphemeGlyphOptions = {
  color?: string | null;
  hanziScript?: HanziScript;
  /**
   * Skip tone coloring and set `color` to `null` so `GraphemeFontGlyph`'s `text-foreground`
   * className wins. Ignored when `color` is passed explicitly.
   */
  uncolored?: boolean;
};

function defaultGlyphColor(colorScheme: ResolvedColorScheme): string {
  return colorScheme === "dark" ? "#ffffff" : "#000000";
}

/**
 * Returns a function that resolves a grapheme glyph using the user's `hanziScript` and tone colors.
 * Every glyph draws at full strength, whether or not the learner has studied it yet.
 */
export function useGetGraphemeGlyph(): (
  original: string,
  options?: UseGetGraphemeGlyphOptions,
) => GraphemeGlyph {
  const colorScheme = useTheme();
  const { hanziScript } = useActorUser();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  const isToneColorsEnabled = useEffectiveIsToneColorsEnabled();
  const toneColors = useActorToneColors();
  const getGraphemePrevalence = useGetGraphemePrevalence();
  const convertersQueryOptions = getHanziScriptConvertersQueryOptions();
  const { data: converters } = useQuery(convertersQueryOptions);
  return useCallback(
    (original: string, options: UseGetGraphemeGlyphOptions = {}) => {
      const { color: colorOverride, hanziScript: hanziScriptOverride, uncolored } = options;
      const targetHanziScript = availableHanziScript(
        hanziScriptOverride ?? hanziScript,
        traditionalCharactersEnabled,
      );
      let color: string | null;
      if (getGraphemePrevalence(original).asCharacter === "obscure") {
        color = defaultGlyphColor(colorScheme);
      } else if (colorOverride !== undefined) {
        color = colorOverride;
      } else if (uncolored) {
        color = null;
      } else {
        const pinyin = pinyinForGrapheme(original);
        const tone = pinyin == null ? null : toneForPinyin(pinyin);
        color =
          !isToneColorsEnabled || tone === null ? defaultGlyphColor(colorScheme) : toneColors[tone];
      }
      const display =
        converters === undefined || targetHanziScript === hanziScript
          ? original
          : convertCharacter(original, hanziScript, targetHanziScript, converters);
      return getGraphemeGlyph(original, { color, display });
    },
    [
      colorScheme,
      converters,
      getGraphemePrevalence,
      hanziScript,
      traditionalCharactersEnabled,
      isToneColorsEnabled,
      toneColors,
    ],
  );
}
